import { cn } from '@/lib/utils';

/**
 * Compact KPI tile — a label, a big value, and an optional sub-line.
 * `tone` tints the value for at-a-glance signal (e.g. overdue → destructive).
 * When `onClick` is set the whole tile becomes a button that drills into a filtered grid.
 */
export function StatCard({
  label,
  value,
  sub,
  tone = 'default',
  onClick,
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'default' | 'success' | 'warning' | 'destructive';
  onClick?: () => void;
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    destructive: 'text-red-600 dark:text-red-400',
  }[tone];

  const className = cn(
    // flex-col: a <button> centres its content vertically, which misaligned tiles with and without a sub-line.
    'flex flex-col justify-start rounded-lg border border-border bg-card p-4 text-left shadow-sm transition-colors',
    onClick && 'cursor-pointer hover:border-ring hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
  );

  const body = (
    <>
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums tracking-tight', toneClass)}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </>
  );

  if (onClick) {
    return <button type="button" onClick={onClick} className={className}>{body}</button>;
  }
  return <div className={className}>{body}</div>;
}
